/**
 * Saved Views Store
 * 
 * Manages saved view configurations with Supabase persistence.
 * Follows the hybrid persistence pattern (optimistic local + Supabase sync).
 */

import { create } from 'zustand';
// Removed persist - Supabase is now the primary data store
import {
  type SavedView,
  type SavedViewEntityType,
  type SavedViewVisibility,
  createSavedView,
  SYSTEM_VIEWS,
  toSupabaseFormat,
  fromSupabaseFormat,
} from '../../types/prioritization/savedViews';
import {
  isSupabaseAvailable,
  getSafeSupabaseClient,
  insertRecord,
  updateRecord,
  deleteRecord,
  getCurrentWorkspaceId,
  getAuthenticatedUserId,
  isInDemoMode,
} from '../../lib/supabaseSync';
import { useUserStore } from './userStore';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import type { ViewMode, SortState, ActiveFilter } from '../../components/collection/types';

// ============================================================================
// Types
// ============================================================================

const TABLE_NAME = 'saved_views';
const STORE_VERSION = 1;

/** created_by for a view that has not (yet) been tied to a signed-in user. */
const LOCAL_OWNER = 'local';

/**
 * saved_views is owner-scoped RLS (mig 728): anon has no access, and a row
 * must carry created_by = auth.uid(). Demo mode runs without a real session,
 * so its views stay local to the tab rather than failing every write.
 */
function shouldPersist(): boolean {
  return isSupabaseAvailable() && !isInDemoMode();
}

interface SyncState {
  isLoading: boolean;
  lastSyncAt: Date | null;
  error: string | null;
}

interface SavedViewsState {
  /** All saved views */
  views: SavedView[];
  
  /** Sync state */
  syncState: SyncState;
  
  /** Store version for migrations */
  version: number;
}

interface SavedViewsActions {
  // CRUD Operations
  
  /** Create a new saved view */
  createView: (params: {
    name: string;
    entityType: SavedViewEntityType;
    filters?: ActiveFilter<string>[];
    sort?: SortState<string>;
    groupBy?: string;
    viewMode?: ViewMode;
    columns?: SavedView['columns'];
    visibility?: SavedViewVisibility;
    icon?: string;
    navSection?: string;
    isPinned?: boolean;
    description?: string;
  }) => SavedView;
  
  /** Update an existing view */
  updateView: (id: string, updates: Partial<SavedView>) => void;
  
  /** Delete a view (system views cannot be deleted) */
  deleteView: (id: string) => boolean;
  
  /** Duplicate a view */
  duplicateView: (id: string, newName?: string) => SavedView | null;
  
  // View access
  
  /** Get view by ID */
  getViewById: (id: string) => SavedView | undefined;
  
  /** Get views by entity type */
  getViewsByEntityType: (entityType: SavedViewEntityType) => SavedView[];
  
  /** Get views for navigation (pinned or in specific section) */
  getNavViews: (section?: string) => SavedView[];
  
  /** Get pinned views */
  getPinnedViews: () => SavedView[];
  
  /** Get system views */
  getSystemViews: () => SavedView[];
  
  /** Get user views (non-system) */
  getUserViews: (userId: string) => SavedView[];
  
  /** Get default view for entity type */
  getDefaultView: (entityType: SavedViewEntityType) => SavedView | undefined;
  
  // View actions
  
  /** Set as default view for entity type */
  setDefaultView: (id: string) => void;
  
  /** Toggle pinned status */
  togglePinned: (id: string) => void;
  
  /** Record view access (updates lastAccessedAt and accessCount) */
  recordAccess: (id: string) => void;
  
  /** Archive a view */
  archiveView: (id: string) => void;
  
  // Sync
  
  /** Fetch views from Supabase */
  fetchFromSupabase: () => Promise<void>;
  
  /** Initialize system views */
  initializeSystemViews: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const createInitialSyncState = (): SyncState => ({
  isLoading: false,
  lastSyncAt: null,
  error: null,
});

// ============================================================================
// Store
// ============================================================================

export const useSavedViewsStore = create<SavedViewsState & SavedViewsActions>()((set, get) => ({
      views: [],
      syncState: createInitialSyncState(),
      version: STORE_VERSION,
      
      // ========================================================================
      // CRUD Operations
      // ========================================================================
      
      createView: (params) => {
        const state = get();
        const workspaceId = getCurrentWorkspaceId();
        
        const view = createSavedView({
          name: params.name,
          entityType: params.entityType,
          // Replaced with auth.uid() before the row is written (RLS checks it).
          createdBy: LOCAL_OWNER,
          createdByName: useUserStore.getState().userName || undefined,
          filters: params.filters || [],
          sort: params.sort || { field: 'createdAt', direction: 'desc' },
          groupBy: params.groupBy,
          viewMode: params.viewMode || 'list',
          columns: params.columns,
          visibility: params.visibility || 'private',
          icon: params.icon,
          navSection: params.navSection,
          isPinned: params.isPinned,
          description: params.description,
          workspaceId,
          status: 'active',
        });
        
        // Optimistic update
        set({ views: [...state.views, view] });
        
        // Persist to Supabase as the signed-in user; without a session the view stays local.
        if (shouldPersist()) {
          void (async () => {
            const authUserId = await getAuthenticatedUserId();
            if (!authUserId) return;
            const owned: SavedView = { ...view, createdBy: authUserId };
            set(s => ({ views: s.views.map(v => (v.id === view.id ? { ...v, createdBy: authUserId } : v)) }));
            const result = await insertRecord(TABLE_NAME, toSupabaseFormat(owned));
            if (result.error) {
              console.error('Failed to save view to Supabase:', result.error);
              window.dispatchEvent(new CustomEvent('store-warning', {
                detail: { message: `Failed to save view: ${result.error}` }
              }));
            }
          })();
        }
        
        return view;
      },
      
      updateView: (id, updates) => {
        const state = get();
        const view = state.views.find(v => v.id === id);
        
        if (!view) return;
        if (view.isSystem && updates.isSystem === false) return; // Can't un-system a system view
        
        const updatedView: SavedView = {
          ...view,
          ...updates,
          updatedAt: new Date(),
        };
        
        // Optimistic update
        set({
          views: state.views.map(v => v.id === id ? updatedView : v),
        });
        
        // Persist to Supabase
        if (shouldPersist()) {
          updateRecord(TABLE_NAME, id, toSupabaseFormat(updatedView)).then(result => {
            if (result.error) {
              console.error('Failed to update view in Supabase:', result.error);
              dispatchStoreError('Saved Views', 'update view', result.error);
            }
          });
        }
      },
      
      deleteView: (id) => {
        const state = get();
        const view = state.views.find(v => v.id === id);
        
        if (!view) return false;
        if (view.isSystem) {
          console.warn('Cannot delete system view');
          return false;
        }
        
        // Optimistic update
        set({
          views: state.views.filter(v => v.id !== id),
        });
        
        // Persist to Supabase
        if (shouldPersist()) {
          deleteRecord(TABLE_NAME, id).then(result => {
            if (result.error) {
              console.error('Failed to delete view from Supabase:', result.error);
              dispatchStoreError('Saved Views', 'delete view', result.error);
            }
          });
        }
        
        return true;
      },
      
      duplicateView: (id, newName) => {
        const state = get();
        const original = state.views.find(v => v.id === id);
        
        if (!original) return null;
        
        const duplicated = get().createView({
          name: newName || `${original.name} (copy)`,
          entityType: original.entityType,
          filters: [...original.filters],
          sort: { ...original.sort },
          groupBy: original.groupBy,
          viewMode: original.viewMode,
          columns: original.columns ? [...original.columns] : undefined,
          visibility: 'private', // Duplicates are private by default
          icon: original.icon,
          navSection: original.navSection,
          description: original.description,
        });
        
        return duplicated;
      },
      
      // ========================================================================
      // View Access
      // ========================================================================
      
      getViewById: (id) => {
        return get().views.find(v => v.id === id);
      },
      
      getViewsByEntityType: (entityType) => {
        return get().views.filter(v => 
          v.entityType === entityType && v.status === 'active'
        );
      },
      
      getNavViews: (section) => {
        const views = get().views.filter(v => 
          v.status === 'active' && 
          (v.isPinned || v.navSection)
        );
        
        if (section) {
          return views.filter(v => v.navSection === section);
        }
        
        return views.sort((a, b) => (a.navOrder || 0) - (b.navOrder || 0));
      },
      
      getPinnedViews: () => {
        return get().views.filter(v => v.isPinned && v.status === 'active');
      },
      
      getSystemViews: () => {
        return get().views.filter(v => v.isSystem);
      },
      
      getUserViews: (userId) => {
        return get().views.filter(v => 
          !v.isSystem && 
          v.createdBy === userId && 
          v.status === 'active'
        );
      },
      
      getDefaultView: (entityType) => {
        return get().views.find(v => 
          v.entityType === entityType && 
          v.isDefault && 
          v.status === 'active'
        );
      },
      
      // ========================================================================
      // View Actions
      // ========================================================================
      
      setDefaultView: (id) => {
        const state = get();
        const view = state.views.find(v => v.id === id);
        
        if (!view) return;
        
        // Unset current default for this entity type
        const updatedViews = state.views.map(v => {
          if (v.entityType === view.entityType && v.isDefault) {
            return { ...v, isDefault: false, updatedAt: new Date() };
          }
          if (v.id === id) {
            return { ...v, isDefault: true, updatedAt: new Date() };
          }
          return v;
        });
        
        set({ views: updatedViews });
        
        // Sync to Supabase (simplified - in production, use transaction)
        if (shouldPersist()) {
          updatedViews
            .filter(v => v.entityType === view.entityType)
            .forEach(v => {
              updateRecord(TABLE_NAME, v.id, { is_default: v.isDefault }).then(result => {
                if (result.error) {
                  console.error('Failed to set default view in Supabase:', result.error);
                  dispatchStoreError('Saved Views', 'set default view', result.error);
                }
              });
            });
        }
      },
      
      togglePinned: (id) => {
        const view = get().views.find(v => v.id === id);
        if (view) {
          get().updateView(id, { isPinned: !view.isPinned });
        }
      },
      
      recordAccess: (id) => {
        const view = get().views.find(v => v.id === id);
        if (view) {
          get().updateView(id, {
            lastAccessedAt: new Date(),
            accessCount: (view.accessCount || 0) + 1,
          });
        }
      },
      
      archiveView: (id) => {
        const view = get().views.find(v => v.id === id);
        if (view && !view.isSystem) {
          get().updateView(id, { status: 'archived' });
        }
      },
      
      // ========================================================================
      // Sync
      // ========================================================================
      
      fetchFromSupabase: async () => {
        if (!shouldPersist()) {
          get().initializeSystemViews();
          return;
        }
        
        set(state => ({
          syncState: { ...state.syncState, isLoading: true, error: null },
        }));
        
        try {
          // Raw rows, NOT fetchAll: fetchAll camel-cases every row (fromDbFormat),
          // but fromSupabaseFormat reads the wire shape (`entity_type`,
          // `group_by`, `view_mode`, `created_at`, …). Fed camel-cased rows it got
          // `undefined` for every multi-word column — so a saved view reloaded
          // from the database lost its entity type, grouping, view mode, quick
          // filters and ownership, and its dates were Invalid Date (recordAccess
          // then threw on the next toISOString()).
          const supabase = getSafeSupabaseClient();
          if (!supabase) throw new Error('Supabase not configured');

          // Demo mode never reaches here (shouldPersist): RLS scopes rows to the caller.
          const { data, error } = await supabase
            .from(TABLE_NAME)
            .select('*')
            .order('created_at', { ascending: true });

          if (error) {
            throw new Error(error.message);
          }

          const views = ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseFormat);
          
          // Merge with system views (system views may not be in DB)
          const systemViewIds = new Set(views.filter(v => v.isSystem).map(v => v.name));
          const missingSystemViews = get().getSystemViews().filter(v => 
            !systemViewIds.has(v.name)
          );
          
          set({
            views: [...views, ...missingSystemViews],
            syncState: {
              isLoading: false,
              lastSyncAt: new Date(),
              error: null,
            },
          });
        } catch (error) {
          console.error('Failed to fetch views:', error);
          set(state => ({
            syncState: {
              ...state.syncState,
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch views',
            },
          }));
          
          // Initialize system views even on error
          get().initializeSystemViews();
        }
      },
      
      initializeSystemViews: () => {
        const state = get();
        const existingSystemNames = new Set(
          state.views.filter(v => v.isSystem).map(v => v.name)
        );
        
        const now = new Date();
        const newSystemViews: SavedView[] = SYSTEM_VIEWS
          .filter(sv => !existingSystemNames.has(sv.name))
          .map(sv => ({
            ...sv,
            id: `system-${sv.entityType}-${sv.name.toLowerCase().replace(/\s+/g, '-')}`,
            createdAt: now,
            updatedAt: now,
          }));
        
        if (newSystemViews.length > 0) {
          set({
            views: [...state.views, ...newSystemViews],
          });
        }
      },
    }));

// Initialize system views on module load
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useSavedViewsStore.getState().initializeSystemViews();
  }, 0);
}

export default useSavedViewsStore;
